import { useCallback, useMemo, useState } from "react";

import {
  defaultDataForNodeType,
  makeEmptyGraph,
  makeId,
  validateGraph,
  type GraphValidationError,
} from "@/lib/productGraph";
import type {
  CreateEnterpriseProductPayload,
  EnterpriseProduct,
  ProductEdge,
  ProductGraph,
  ProductNode,
  ProductNodeType,
} from "@/services/platformEnterprisesService";

export interface ProductBuilderHeader {
  displayName: string;
  description: string;
  category: string;
  amountMinor: number;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  slug?: string;
}

export interface ProductBuilderState {
  header: ProductBuilderHeader;
  graph: ProductGraph;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  isDirty: boolean;
  errors: GraphValidationError[];
}

export interface UseProductBuilderStateOptions {
  initial?: EnterpriseProduct | null;
}

export interface UseProductBuilderStateResult extends ProductBuilderState {
  setHeader: (patch: Partial<ProductBuilderHeader>) => void;
  setSelectedNodeId: (id: string | null) => void;
  setSelectedEdgeId: (id: string | null) => void;
  addNode: (
    type: ProductNodeType,
    position?: { x: number; y: number },
    dataOverrides?: Record<string, unknown>,
  ) => string;
  updateNodeData: (id: string, data: Record<string, unknown>) => void;
  updateNodePosition: (
    id: string,
    position: { x: number; y: number },
    commit?: boolean,
  ) => void;
  removeNode: (id: string) => void;
  addEdge: (edge: Omit<ProductEdge, "id">) => string;
  removeEdge: (id: string) => void;
  reconnectEdge: (
    edgeId: string,
    next: { source: string; target: string; sourceHandle?: string },
  ) => void;
  setGraph: (graph: ProductGraph) => void;
  toPayload: () => CreateEnterpriseProductPayload;
  resetDirty: () => void;
}

function buildInitialHeader(initial?: EnterpriseProduct | null): ProductBuilderHeader {
  return {
    displayName: initial?.displayName ?? "",
    description: initial?.description ?? "",
    category: initial?.category ?? "",
    amountMinor: initial?.amountMinor ?? 0,
    currency: initial?.currency ?? "UYU",
    isActive: initial?.isActive ?? true,
    sortOrder: initial?.sortOrder ?? 0,
    slug: initial?.slug,
  };
}

function buildInitialGraph(initial?: EnterpriseProduct | null): ProductGraph {
  if (initial?.graph) return initial.graph;
  return makeEmptyGraph();
}

export function useProductBuilderState(
  options: UseProductBuilderStateOptions = {},
): UseProductBuilderStateResult {
  const { initial } = options;

  const [header, setHeaderState] = useState<ProductBuilderHeader>(() =>
    buildInitialHeader(initial),
  );
  const [graph, setGraphState] = useState<ProductGraph>(() => buildInitialGraph(initial));
  const [selectedNodeId, setSelectedNodeIdState] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeIdState] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const setSelectedNodeId = useCallback((id: string | null) => {
    setSelectedNodeIdState(id);
    if (id !== null) setSelectedEdgeIdState(null);
  }, []);

  const setSelectedEdgeId = useCallback((id: string | null) => {
    setSelectedEdgeIdState(id);
    if (id !== null) setSelectedNodeIdState(null);
  }, []);

  const errors = useMemo(() => validateGraph(graph), [graph]);

  const markDirty = useCallback(() => setIsDirty(true), []);

  const setHeader = useCallback(
    (patch: Partial<ProductBuilderHeader>) => {
      setHeaderState((prev) => ({ ...prev, ...patch }));
      markDirty();
    },
    [markDirty],
  );

  const setGraph = useCallback(
    (next: ProductGraph) => {
      setGraphState(next);
      markDirty();
    },
    [markDirty],
  );

  const addNode = useCallback(
    (
      type: ProductNodeType,
      position?: { x: number; y: number },
      dataOverrides?: Record<string, unknown>,
    ): string => {
      const id = makeId(type);
      const newNode: ProductNode = {
        id,
        type,
        position: position ?? { x: 200, y: 200 },
        data: { ...defaultDataForNodeType(type), ...(dataOverrides ?? {}) },
      };
      setGraphState((prev) => ({ ...prev, nodes: [...prev.nodes, newNode] }));
      markDirty();
      return id;
    },
    [markDirty],
  );

  const updateNodeData = useCallback(
    (id: string, data: Record<string, unknown>) => {
      setGraphState((prev) => ({
        ...prev,
        nodes: prev.nodes.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, ...data } } : n,
        ),
      }));
      markDirty();
    },
    [markDirty],
  );

  const updateNodePosition = useCallback(
    (id: string, position: { x: number; y: number }, commit = true) => {
      setGraphState((prev) => ({
        ...prev,
        nodes: prev.nodes.map((n) => (n.id === id ? { ...n, position } : n)),
      }));
      if (commit) markDirty();
    },
    [markDirty],
  );

  const removeNode = useCallback(
    (id: string) => {
      let didRemove = false;
      setGraphState((prev) => {
        const target = prev.nodes.find((n) => n.id === id);
        if (!target || target.type === "start") return prev;
        didRemove = true;
        return {
          ...prev,
          nodes: prev.nodes.filter((n) => n.id !== id),
          edges: prev.edges.filter((e) => e.source !== id && e.target !== id),
        };
      });
      if (didRemove) {
        setSelectedNodeIdState((curr) => (curr === id ? null : curr));
        markDirty();
      }
    },
    [markDirty],
  );

  const addEdge = useCallback(
    (edge: Omit<ProductEdge, "id">): string => {
      const id = makeId("e");
      setGraphState((prev) => {
        const exists = prev.edges.some(
          (e) =>
            e.source === edge.source &&
            e.target === edge.target &&
            (e.sourceHandle ?? null) === (edge.sourceHandle ?? null),
        );
        if (exists) return prev;
        return { ...prev, edges: [...prev.edges, { id, ...edge }] };
      });
      markDirty();
      return id;
    },
    [markDirty],
  );

  const removeEdge = useCallback(
    (id: string) => {
      setGraphState((prev) => ({
        ...prev,
        edges: prev.edges.filter((e) => e.id !== id),
      }));
      setSelectedEdgeIdState((curr) => (curr === id ? null : curr));
      markDirty();
    },
    [markDirty],
  );

  const reconnectEdge = useCallback(
    (
      edgeId: string,
      next: { source: string; target: string; sourceHandle?: string },
    ) => {
      setGraphState((prev) => ({
        ...prev,
        edges: prev.edges.map((e) =>
          e.id === edgeId
            ? {
                ...e,
                source: next.source,
                target: next.target,
                sourceHandle: next.sourceHandle,
              }
            : e,
        ),
      }));
      markDirty();
    },
    [markDirty],
  );

  const toPayload = useCallback((): CreateEnterpriseProductPayload => {
    return {
      displayName: header.displayName,
      description: header.description,
      category: header.category,
      amountMinor: header.amountMinor,
      currency: header.currency,
      isActive: header.isActive,
      sortOrder: header.sortOrder,
      graph,
      ...(header.slug ? { slug: header.slug } : {}),
    };
  }, [header, graph]);

  const resetDirty = useCallback(() => setIsDirty(false), []);

  return {
    header,
    graph,
    selectedNodeId,
    selectedEdgeId,
    isDirty,
    errors,
    setHeader,
    setSelectedNodeId,
    setSelectedEdgeId,
    addNode,
    updateNodeData,
    updateNodePosition,
    removeNode,
    addEdge,
    removeEdge,
    reconnectEdge,
    setGraph,
    toPayload,
    resetDirty,
  };
}
